import { useEffect, useState } from 'react';
import JsPDF from 'jspdf';
import Layout from '../../components/layout/Layout';
import PageCard from '../../components/ui/PageCard';
import MagazinePageHeader from '../magazine/MagazinePageHeader';
import MagazineNewButton from '../magazine/MagazineNewButton';
import MagazineFormModal from '../magazine/MagazineFormModal';
import { useAuth } from '../../hooks/useAuth';
import { getPublications } from '../../api/magazineService';
import {
  TEMPLATE_PLACEHOLDERS,
  createContractTemplate,
  deleteContractTemplate,
  getContractTemplates,
  updateContractTemplate,
} from '../../api/contractService';

const emptyForm = { name: '', body: '', publication_id: '' };

const defaultBody = `Magazine Advertising Agreement

This agreement is between Torch Events and {{company_name}}.

Company logo: {{company_logo}}

Contact: {{contact_name}} ({{contact_email}})

Publications: {{publications}}

Line items:
{{line_items_table}}

Total price: {{total_price}}

Sales representative: {{sales_rep}}
Date: {{date}}

Proposal notes: {{proposal_notes}}

Signed by: {{signer_name}}
Signed on: {{signed_date}}
`;

const previewValues: Record<string, string> = {
  company_name: 'Example Company Ltd',
  company_logo: '[Company logo]',
  contact_name: 'Jane Smith',
  contact_email: 'jane@example.com',
  publication: 'Example Publication',
  publications: 'Example Publication',
  issue: 'Spring 2027',
  ad_slot: 'Inside front cover',
  ad_size: 'Full page',
  price: '£1,250.00',
  line_items_table: 'Example Publication — Full-page advert — £1,250.00',
  total_price: '£1,250.00',
  proposal_notes: 'Example proposal notes appear here.',
  proposal_status: 'Draft',
  sales_rep: 'Alex Taylor',
  booking_status: 'Confirmed',
  booking_notes: 'Example booking notes appear here.',
  signer_name: 'Jane Smith',
  signed_date: new Date().toLocaleDateString('en-GB'),
  date: new Date().toLocaleDateString('en-GB'),
};

const renderPreviewBody = (body: string, contractFrom: string) =>
  body.replace(/{{\s*([a-z_]+)\s*}}/gi, (placeholder, key: string) =>
    previewValues[key] || placeholder
  );

const ContractTemplates = () => {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState<any[]>([]);
  const [publications, setPublications] = useState<any[]>([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const [data, pubs] = await Promise.all([getContractTemplates(), getPublications()]);
    setItems(data);
    setPublications(pubs);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const closeForm = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(false);
  };

  const openNewForm = () => {
    setForm({ name: '', body: defaultBody, publication_id: '' });
    setEditingId(null);
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isAdmin) return;
    const payload = {
      name: form.name,
      body: form.body,
      publication_id: form.publication_id ? Number(form.publication_id) : null,
    };
    if (editingId) {
      await updateContractTemplate(editingId, payload);
    } else {
      await createContractTemplate(payload);
    }
    closeForm();
    load();
  };

  const handleEdit = (item: any) => {
    setEditingId(item.id);
    setForm({
      name: item.name,
      body: item.body,
      publication_id: item.publication_id ? String(item.publication_id) : '',
    });
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!isAdmin || !window.confirm('Delete this template?')) return;
    try {
      await deleteContractTemplate(id);
      load();
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Failed to delete template.');
    }
  };

  const previewPdf = (name: string, body: string) => {
    const pdf = new JsPDF({ unit: 'mm', format: 'a4' });
    const margin = 20;
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const maxWidth = pageWidth - margin * 2;
    const lineHeight = 6;
    let y = margin;

    pdf.setProperties({ title: `${name} preview` });
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(16);
    pdf.text(name || 'Contract template', margin, y);
    y += 10;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(11);

    const lines = pdf.splitTextToSize(renderPreviewBody(body, 'Selected proposal contract company'), maxWidth) as string[];
    lines.forEach((line) => {
      if (y > pageHeight - margin) {
        pdf.addPage();
        y = margin;
      }
      pdf.text(line, margin, y);
      y += lineHeight;
    });

    const url = pdf.output('bloburl');
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Layout>
      <MagazinePageHeader
        title="Contract templates"
        description="Reusable templates for magazine advertising contracts. Scope a template to a publication or leave global."
        action={isAdmin ? <MagazineNewButton onClick={openNewForm} label="New template" /> : undefined}
      />

      <MagazineFormModal
        open={showForm}
        title={editingId ? 'Edit template' : 'New template'}
        onClose={closeForm}
      >
        <form onSubmit={handleSubmit} className="grid min-w-0 gap-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-600">Name</label>
            <input
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-600">Publication (optional)</label>
            <select
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
              value={form.publication_id}
              onChange={(e) => setForm({ ...form, publication_id: e.target.value })}
            >
              <option value="">All publications (global)</option>
              {publications.map((pub) => (
                <option key={pub.id} value={pub.id}>{pub.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-600">Body</label>
            <p className="mb-2 text-xs text-gray-500">Placeholders: {TEMPLATE_PLACEHOLDERS}</p>
            <textarea
              className="w-full min-w-0 rounded border border-gray-300 px-3 py-2 font-mono text-sm"
              rows={14}
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              required
            />
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => previewPdf(form.name || 'Contract template', form.body)}
              disabled={!form.body.trim()}
              className="rounded border border-torch-primary px-4 py-2 text-sm text-torch-primary hover:bg-purple-50 disabled:opacity-50"
            >
              Preview PDF
            </button>
            <button
              type="submit"
              className="rounded bg-torch-primary px-4 py-2 text-sm font-medium text-white hover:bg-torch-primary-dark"
            >
              {editingId ? 'Update' : 'Create template'}
            </button>
            <button
              type="button"
              onClick={closeForm}
              className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-700"
            >
              Cancel
            </button>
          </div>
        </form>
      </MagazineFormModal>

      <PageCard>
        {loading ? (
          <p className="text-sm text-gray-500">Loading...</p>
        ) : items.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-500">No templates yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-gray-500">
                  <th className="py-2 pr-4">Name</th>
                  <th className="py-2 pr-4">Publication</th>
                  <th className="py-2 pr-4">Updated</th>
                  <th className="py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} className="border-b border-gray-100">
                    <td className="py-3 pr-4 font-medium text-gray-900">{item.name}</td>
                    <td className="py-3 pr-4 text-gray-600">{item.publication?.name || 'Global'}</td>
                    <td className="py-3 pr-4 text-gray-600">
                      {item.updatedAt ? new Date(item.updatedAt).toLocaleDateString() : '—'}
                    </td>
                    <td className="py-3">
                      <div className="flex gap-2">
                        <button onClick={() => previewPdf(item.name, item.body)} className="text-sm text-blue-600 hover:underline">
                          Preview PDF
                        </button>
                        <button onClick={() => handleEdit(item)} className="text-sm text-torch-primary hover:underline">
                          Edit
                        </button>
                        <button onClick={() => handleDelete(item.id)} className="text-sm text-red-600 hover:underline">
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </PageCard>
    </Layout>
  );
};

export default ContractTemplates;
